/** XeAgents.ai - Zone-Based Credit Rate Settings */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Save, Loader2, IndianRupee, Globe, Phone, Mic, RefreshCw, Calculator, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Badge } from "@/components/ui/badge";

interface ZoneCreditSettingsData {
  settings: {
    indiaRateInr: number;
    internationalRateInr: number;
    elevenlabsRateInr: number;
    novaSonicRateInr: number;
    baseCurrency: string;
    usdToInrRate: number;
    enabled: boolean;
  };
  rates: Array<{
    zone: string;
    telephonyProvider: string;
    voiceProvider: string;
    creditsPerMinute: number;
    rateInr: number;
    costPerMinuteUsd: number;
    description: string;
  }>;
  description: Record<string, string>;
}

interface CreditPresetData {
  amountInr: number;
  baseRate: number;
  totalCredits: number;
  scenarios: Array<{
    zone: string;
    provider: string;
    voiceProvider: string;
    rateInr: number;
    creditsPerMinute: number;
    minutesAvailable: number;
    description: string;
  }>;
}

export default function ZoneCreditSettings() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    indiaRateInr: 4.5,
    internationalRateInr: 5.5,
    elevenlabsRateInr: 18.0,
    novaSonicRateInr: 12.0,
    usdToInrRate: 85,
    enabled: true,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [calculatorAmount, setCalculatorAmount] = useState(50000);

  // Fetch current settings
  const { data, isLoading, error, refetch } = useQuery<ZoneCreditSettingsData>({
    queryKey: ["/api/admin/zone-credit-settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/zone-credit-settings");
      return res.json();
    },
  });

  // Fetch credit presets for calculator
  const { data: presetData, refetch: refetchPresets } = useQuery<CreditPresetData>({
    queryKey: ["/api/admin/credit-calculator", calculatorAmount],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/credit-calculator?amount=${calculatorAmount}`);
      return res.json();
    },
    enabled: calculatorAmount > 0,
  });

  // Update form when data loads
  useEffect(() => {
    if (data?.settings) {
      setFormData({
        indiaRateInr: data.settings.indiaRateInr,
        internationalRateInr: data.settings.internationalRateInr,
        elevenlabsRateInr: data.settings.elevenlabsRateInr,
        novaSonicRateInr: data.settings.novaSonicRateInr,
        usdToInrRate: data.settings.usdToInrRate,
        enabled: data.settings.enabled,
      });
      setHasChanges(false);
    }
  }, [data]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (settings: typeof formData) => {
      const res = await apiRequest("PUT", "/api/admin/zone-credit-settings", settings);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to save settings");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Settings Saved",
        description: "Zone credit settings have been updated successfully.",
      });
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/zone-credit-settings"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleInputChange = (field: keyof typeof formData, value: number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <Alert variant="destructive">
            <AlertDescription>Failed to load zone credit settings. Please try again.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <IndianRupee className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle>Zone-Based Billing Rates</CardTitle>
                <CardDescription>
                  Configure INR billing rates for Indian organizations. Credits remain 1 credit = 1 minute. INR rates are for revenue tracking.
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges || saveMutation.isPending}>
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => handleInputChange("enabled", checked)}
              />
              <div>
                <p className="font-medium">Enable Zone-Based Billing</p>
                <p className="text-sm text-muted-foreground">
                  Track different INR billing rates for India vs international calls. Credits: 1 credit = 1 minute (always).
                </p>
              </div>
            </div>
            <Badge variant={formData.enabled ? "default" : "secondary"}>
              {formData.enabled ? "Active" : "Disabled"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Rate Configuration */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* India Rates */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">India Call Rates</CardTitle>
            </div>
            <CardDescription>Rates for calls using Plivo (India telephony)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                India Rate (INR/min)
                <InfoTooltip content="Rate charged per minute for calls from Indian numbers to Indian numbers using Plivo + Sarvam/Bulbul" />
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="100"
                  value={formData.indiaRateInr}
                  onChange={(e) => handleInputChange("indiaRateInr", parseFloat(e.target.value) || 0)}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Your cost: ~₹3.23/min (Plivo + Sarvam)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* International Rates */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">International Call Rates</CardTitle>
            </div>
            <CardDescription>Rates for calls using Twilio (international telephony)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                International Rate (INR/min)
                <InfoTooltip content="Rate charged per minute for calls from Indian orgs to non-Indian numbers using Twilio + Sarvam/Bulbul" />
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="100"
                  value={formData.internationalRateInr}
                  onChange={(e) => handleInputChange("internationalRateInr", parseFloat(e.target.value) || 0)}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Your cost: ~₹4.00/min (Twilio + Sarvam)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Premium Voice Rates */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">Premium Voice Provider Rates</CardTitle>
          </div>
          <CardDescription>Higher rates for premium voice AI providers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                ElevenLabs Rate (INR/min)
                <InfoTooltip content="Rate for calls using ElevenLabs premium voice AI" />
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="500"
                  value={formData.elevenlabsRateInr}
                  onChange={(e) => handleInputChange("elevenlabsRateInr", parseFloat(e.target.value) || 0)}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Your cost: ~₹21.85/min (Twilio + ElevenLabs)
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Nova Sonic Rate (INR/min)
                <InfoTooltip content="Rate for calls using AWS Nova Sonic voice AI" />
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="500"
                  value={formData.novaSonicRateInr}
                  onChange={(e) => handleInputChange("novaSonicRateInr", parseFloat(e.target.value) || 0)}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Your cost: ~₹15.05/min (Twilio + Nova Sonic)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Currency Settings</CardTitle>
          <CardDescription>Exchange rate for USD to INR conversion</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs space-y-2">
            <Label className="flex items-center gap-2">
              USD to INR Rate
              <InfoTooltip content="Current USD to INR exchange rate for cost calculations" />
            </Label>
            <Input
              type="number"
              step="0.5"
              min="50"
              max="150"
              value={formData.usdToInrRate}
              onChange={(e) => handleInputChange("usdToInrRate", parseFloat(e.target.value) || 85)}
            />
            <p className="text-xs text-muted-foreground">
              Current rate: 1 USD = ₹{formData.usdToInrRate}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Current Rates Summary */}
      {data?.rates && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Rate Summary</CardTitle>
            <CardDescription>Overview of all configured rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Zone</th>
                    <th className="text-left py-2 px-4">Provider</th>
                    <th className="text-left py-2 px-4">Voice</th>
                    <th className="text-right py-2 px-4">Rate (INR/min)</th>
                    <th className="text-right py-2 px-4">Your Cost (USD)</th>
                    <th className="text-right py-2 px-4">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rates.map((rate, index) => {
                    const rateInr = rate.zone === 'India' ? formData.indiaRateInr :
                                    rate.voiceProvider.includes('ElevenLabs') ? formData.elevenlabsRateInr :
                                    rate.voiceProvider.includes('Nova') ? formData.novaSonicRateInr :
                                    formData.internationalRateInr;
                    const costInr = rate.costPerMinuteUsd * formData.usdToInrRate;
                    const margin = ((rateInr - costInr) / rateInr * 100).toFixed(1);
                    const marginPositive = parseFloat(margin) > 0;

                    return (
                      <tr key={index} className="border-b last:border-0">
                        <td className="py-2 px-4">
                          <Badge variant={rate.zone === 'India' ? 'default' : 'outline'}>
                            {rate.zone}
                          </Badge>
                        </td>
                        <td className="py-2 px-4">{rate.telephonyProvider}</td>
                        <td className="py-2 px-4">{rate.voiceProvider}</td>
                        <td className="text-right py-2 px-4 font-medium">₹{rateInr.toFixed(2)}</td>
                        <td className="text-right py-2 px-4 text-muted-foreground">
                          ${rate.costPerMinuteUsd.toFixed(3)} (~₹{costInr.toFixed(2)})
                        </td>
                        <td className={`text-right py-2 px-4 font-medium ${marginPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {marginPositive ? '+' : ''}{margin}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credit Calculator */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Credit Calculator</CardTitle>
          </div>
          <CardDescription>
            Calculate credits and minutes for a given INR amount. Credits purchased at base rate (₹{formData.indiaRateInr}/credit).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <Label>Customer Payment (INR)</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="1000"
                  min="100"
                  max="10000000"
                  value={calculatorAmount}
                  onChange={(e) => setCalculatorAmount(parseInt(e.target.value) || 0)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-6">
              {[10000, 25000, 50000, 100000].map((amount) => (
                <Button
                  key={amount}
                  variant={calculatorAmount === amount ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCalculatorAmount(amount)}
                >
                  ₹{(amount / 1000).toFixed(0)}K
                </Button>
              ))}
            </div>
          </div>

          {presetData && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Customer Pays</p>
                    <p className="text-2xl font-bold">₹{presetData.amountInr.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Base Rate</p>
                    <p className="text-lg font-medium">₹{presetData.baseRate}/credit</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Credits</p>
                    <p className="text-2xl font-bold text-blue-600">{presetData.totalCredits.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4">Usage Scenario</th>
                      <th className="text-left py-3 px-4">Provider</th>
                      <th className="text-right py-3 px-4">Rate (INR/min)</th>
                      <th className="text-right py-3 px-4">Credits/min</th>
                      <th className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Clock className="h-4 w-4" />
                          Minutes Available
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {presetData.scenarios.map((scenario, index) => (
                      <tr key={index} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Badge variant={scenario.zone === 'India' ? 'default' : 'outline'}>
                              {scenario.zone}
                            </Badge>
                            <span className="text-muted-foreground text-xs">{scenario.description}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm">{scenario.provider} + {scenario.voiceProvider}</span>
                        </td>
                        <td className="text-right py-3 px-4 font-medium">₹{scenario.rateInr.toFixed(2)}</td>
                        <td className="text-right py-3 px-4">
                          <Badge variant="secondary">{scenario.creditsPerMinute}</Badge>
                        </td>
                        <td className="text-right py-3 px-4">
                          <span className="text-lg font-bold text-green-600">
                            {scenario.minutesAvailable.toLocaleString()}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">min</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Alert>
                <AlertDescription className="text-sm">
                  <strong>How it works:</strong> Credits are purchased at the India base rate (₹{formData.indiaRateInr}/credit).
                  International calls consume more credits per minute ({(formData.internationalRateInr / formData.indiaRateInr).toFixed(2)} credits/min)
                  to account for the higher rate.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
